import { ExternalUser, SalesPerson } from "models/customers/customers";
import UserServiceTree from "./serviceTree/UserServiceTree";

import "./UserServiceExpandableListItem.scss";
import {
  FOLDER_ID_BOK_DEKL,
  legacySuffixes
} from "views/documents/state/documentArchiveOptions";
import {
  generateDescendingPairsRange,
  generateDescendingRange
} from "views/documents/state/libs/generate-descending";
import { useRootStore } from "store";

type Props = {
  user: ExternalUser | SalesPerson;
  editing: boolean;
  onEditClick: (user: ExternalUser | SalesPerson) => void;
  isInternal: boolean;
  fpConfig: any;
  onSetFpService: (customerChoice: string, userId: string) => void;
  onToggleService: (userId: string, serviceId: string, value: boolean) => void;
  onToggleChildServices: (
    userId: string,
    serviceIds: string[],
    value: boolean
  ) => void;
  disableFp?: boolean;
};

export default function UserServiceExpandableListItem({
  user,
  editing,
  onEditClick,
  isInternal,
  fpConfig,
  onSetFpService,
  onToggleService,
  onToggleChildServices,
  disableFp
}: Props) {
  const { documentServices } = useRootStore((state) => state.wizard.customer);

  const handleOnClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    onEditClick(user);
  };

  const getClasses = () => {
    const classes = ["expandableListItemWrapper"];

    if (editing) {
      classes.push("open");
    }

    return classes.join(" ");
  };

  const getPrefixAndSuffix = (input: string): [string, string] => {
    const trimmed = input.trim();
    const match = trimmed.match(/(.+?)-([^-/\n]+)?$/);

    if (match) {
      const prefix = match[1] ?? "";
      const suffix = match[2] ?? "";
      return [prefix, suffix];
    }

    // Fallback if nothing matches
    return ["", ""];
  };

  const getUsedLegacyNodes = () => {
    // The used legacy nodes logic must reflect the configuration from
    // documentArchiveOptions in documentArchiveOptions.ts
    const periodsFin = [
      ...generateDescendingRange(2020, 3).map((r) => ({
        periodName: r.toString(),
        sortOrder: 2025 - r
      })),
      ...generateDescendingPairsRange(2021, 3).map((pair) => {
        return {
          periodName: `${pair[0]}/${pair[1]}`,
          sortOrder: 2025 - (pair[0] + pair[1]) / 2
        };
      }),
      ...generateDescendingRange(2018, 14).map((r) => ({
        periodName: r.toString(),
        sortOrder: 2018 - r
      })),
      ...generateDescendingPairsRange(2018, 13).map((pair) => {
        return {
          periodName: `${pair[0]}/${pair[1]}`,
          sortOrder: 2018 + 1 - (pair[0] + pair[1]) / 2
        };
      })
    ];
    const usedLegacyFinFolders = periodsFin.map(
      (period) => `eksk-fin-${period.periodName}-${FOLDER_ID_BOK_DEKL}`
    );

    return usedLegacyFinFolders;
  };

  const filteredServices = documentServices.filter((service) => {
    const servicePrefix = getPrefixAndSuffix(service.id)[0];
    const serviceSuffix = getPrefixAndSuffix(service.id)[1];
    if (getUsedLegacyNodes().includes(servicePrefix)) {
      return true;
    }
    if (
      legacySuffixes.includes(serviceSuffix) &&
      servicePrefix.endsWith(FOLDER_ID_BOK_DEKL)
    ) {
      return false;
    }
    return true;
  });

  const getUserCount = () => {
    const userServices = filteredServices.filter((s) => {
      return s.userIds.includes(user.id);
    });

    return userServices.length;
  };

  const total =
    filteredServices && filteredServices.length !== 0
      ? filteredServices.length
      : 0;
  const userCount =
    filteredServices && filteredServices.length !== 0 ? getUserCount() : 0;

  const serviceCountString = ` ${userCount} aktiva ${
    total - userCount
  } inaktiverade`;

  const editingBox = editing && (
    <UserServiceTree
      fpConfig={fpConfig}
      onSetFpService={onSetFpService}
      userId={user.id}
      isInternal={isInternal}
      onToggleService={onToggleService}
      onToggleChildServices={onToggleChildServices}
      disableFp={disableFp}
    />
  );

  return (
    <li key={user.id} className={getClasses()}>
      <div onClick={(e) => handleOnClick(e)}>
        <span>{user.givenName + " " + user.surName}</span>
        <span>{serviceCountString}</span>
      </div>
      {editingBox}
    </li>
  );
}
